import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api } from '../lib/api';
import { requireAccess } from '../lib/guards';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { PageHeader } from '../components/ui/PageHeader';

interface Objekt {
  id: string;
  kundeId: string;
  objektnummer: string | null;
  bezeichnung: string;
  adresseStrasse: string | null;
  adressePlz: string | null;
  adresseOrt: string | null;
  typ: string | null;
  anzahlEinheiten: number | null;
}

interface Kunde {
  id: string;
  kundennummer: string;
  firmenname: string;
}

export const Route = createFileRoute('/objekte/')({
  beforeLoad: () => requireAccess('/api/v1/objekte'),
  component: ObjektePage,
});

function ObjektePage() {
  const qc = useQueryClient();
  const [kundeFilter, setKundeFilter] = useState('');

  const kundenQ = useQuery({
    queryKey: ['kunden', 'lookup'],
    queryFn: () => api<{ items: Kunde[] }>('/api/v1/kunden'),
  });
  const listQ = useQuery({
    queryKey: ['objekte', kundeFilter],
    queryFn: () =>
      api<{ items: Objekt[] }>(`/api/v1/objekte${kundeFilter ? `?kundeId=${kundeFilter}` : ''}`),
  });
  const [showForm, setShowForm] = useState(false);

  const kundeMap = new Map((kundenQ.data?.items ?? []).map((k) => [k.id, k]));

  return (
    <div className="space-y-4">
      <PageHeader
        title="Objekte"
        description="Gebäude/Liegenschaften pro Kunde. Referenz für Aufträge und Betreuungsverträge."
        actions={
          <Button onClick={() => setShowForm((s) => !s)} variant={showForm ? 'ghost' : 'primary'}>
            {showForm ? 'Abbrechen' : '+ Neues Objekt'}
          </Button>
        }
      />

      <div className="flex items-center gap-3 text-sm">
        <label>
          <span className="text-slate-600 mr-2">Kunde</span>
          <Select
            value={kundeFilter}
            onChange={(e) => setKundeFilter(e.target.value)}
            className="w-72 inline-block"
          >
            <option value="">alle</option>
            {(kundenQ.data?.items ?? []).map((k) => (
              <option key={k.id} value={k.id}>
                {k.kundennummer} — {k.firmenname}
              </option>
            ))}
          </Select>
        </label>
      </div>

      {showForm && kundenQ.data && (
        <ObjektCreateForm
          kunden={kundenQ.data.items}
          defaultKundeId={kundeFilter || kundenQ.data.items[0]?.id || ''}
          onDone={() => {
            setShowForm(false);
            void qc.invalidateQueries({ queryKey: ['objekte'] });
          }}
        />
      )}

      <ErrorBanner error={listQ.error} />

      {listQ.data && (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <table className="w-full">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2 text-left w-28">Nr.</th>
                <th className="px-4 py-2 text-left">Bezeichnung</th>
                <th className="px-4 py-2 text-left">Kunde</th>
                <th className="px-4 py-2 text-left">Adresse</th>
                <th className="px-4 py-2 text-left w-24">Typ</th>
                <th className="px-4 py-2 text-right w-20">Einheiten</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {listQ.data.items.map((o) => {
                const k = kundeMap.get(o.kundeId);
                return (
                  <tr key={o.id} className="text-sm text-slate-800">
                    <td className="px-4 py-2 font-mono text-xs text-slate-600">
                      {o.objektnummer ?? '–'}
                    </td>
                    <td className="px-4 py-2 font-medium">
                      <Link
                        to="/objekte/$id"
                        params={{ id: o.id }}
                        className="text-brand-700 hover:underline"
                      >
                        {o.bezeichnung}
                      </Link>
                    </td>
                    <td className="px-4 py-2 text-slate-600">
                      {k ? `${k.kundennummer} — ${k.firmenname}` : o.kundeId.slice(0, 8)}
                    </td>
                    <td className="px-4 py-2 text-slate-600">
                      {[o.adresseStrasse, o.adressePlz, o.adresseOrt].filter(Boolean).join(', ') ||
                        '–'}
                    </td>
                    <td className="px-4 py-2 text-slate-600">{o.typ ?? '–'}</td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {o.anzahlEinheiten ?? '–'}
                    </td>
                  </tr>
                );
              })}
              {listQ.data.items.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-400">
                    Keine Objekte.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ObjektCreateForm({
  kunden,
  defaultKundeId,
  onDone,
}: {
  kunden: Kunde[];
  defaultKundeId: string;
  onDone: () => void;
}) {
  const [st, setSt] = useState({
    kundeId: defaultKundeId,
    objektnummer: '',
    bezeichnung: '',
    adresseStrasse: '',
    adressePlz: '',
    adresseOrt: '',
    typ: '',
    anzahlEinheiten: '',
  });
  const [error, setError] = useState<unknown>(null);

  const save = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {
        kundeId: st.kundeId,
        bezeichnung: st.bezeichnung,
      };
      if (st.objektnummer.trim()) body['objektnummer'] = st.objektnummer.trim();
      if (st.adresseStrasse.trim()) body['adresseStrasse'] = st.adresseStrasse.trim();
      if (st.adressePlz.trim()) body['adressePlz'] = st.adressePlz.trim();
      if (st.adresseOrt.trim()) body['adresseOrt'] = st.adresseOrt.trim();
      if (st.typ.trim()) body['typ'] = st.typ.trim();
      if (st.anzahlEinheiten.trim()) body['anzahlEinheiten'] = Number(st.anzahlEinheiten);
      return api<{ item: Objekt }>('/api/v1/objekte', { method: 'POST', body });
    },
    onSuccess: () => onDone(),
    onError: (err) => setError(err),
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    save.mutate();
  }

  return (
    <form
      onSubmit={onSubmit}
      className="rounded-lg border border-slate-200 bg-white p-4 space-y-3 shadow-sm"
    >
      <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
        <div className="md:col-span-2">
          <label className="text-xs font-medium text-slate-600">Kunde</label>
          <Select
            required
            value={st.kundeId}
            onChange={(e) => setSt({ ...st, kundeId: e.target.value })}
          >
            {kunden.map((k) => (
              <option key={k.id} value={k.id}>
                {k.kundennummer} — {k.firmenname}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Objekt-Nr.</label>
          <Input
            value={st.objektnummer}
            onChange={(e) => setSt({ ...st, objektnummer: e.target.value })}
          />
        </div>
        <div className="md:col-span-3">
          <label className="text-xs font-medium text-slate-600">Bezeichnung</label>
          <Input
            required
            value={st.bezeichnung}
            onChange={(e) => setSt({ ...st, bezeichnung: e.target.value })}
          />
        </div>
        <div className="md:col-span-3">
          <label className="text-xs font-medium text-slate-600">Straße</label>
          <Input
            value={st.adresseStrasse}
            onChange={(e) => setSt({ ...st, adresseStrasse: e.target.value })}
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">PLZ</label>
          <Input
            value={st.adressePlz}
            onChange={(e) => setSt({ ...st, adressePlz: e.target.value })}
          />
        </div>
        <div className="md:col-span-2">
          <label className="text-xs font-medium text-slate-600">Ort</label>
          <Input
            value={st.adresseOrt}
            onChange={(e) => setSt({ ...st, adresseOrt: e.target.value })}
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Typ</label>
          <Input
            value={st.typ}
            onChange={(e) => setSt({ ...st, typ: e.target.value })}
            placeholder="Wohnhaus"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Einheiten</label>
          <Input
            type="number"
            min="1"
            value={st.anzahlEinheiten}
            onChange={(e) => setSt({ ...st, anzahlEinheiten: e.target.value })}
          />
        </div>
      </div>
      <ErrorBanner error={error} />
      <div className="flex justify-end">
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Speichere…' : 'Objekt anlegen'}
        </Button>
      </div>
    </form>
  );
}
